import { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import { MessageSquare, FileText, History, Sparkles, Pause, Play, RotateCcw } from "lucide-react";
import ambient from "@/assets/hero-ambient.json";
import { EXAMPLE } from "@/content/landingExample";

/*
  HeroAnimation
  -------------
  One fictional story (see content/landingExample.js) told as four short
  highlights over the ambient Lottie orb. The CSS sequence (index.css,
  `.hero-scene`) reveals the cards in story order, about six seconds each,
  runs ONCE when the hero enters view and settles with all four readable.
  Controls: Pause/Play (also pauses the orb), Replay. prefers-reduced-motion
  shows the settled state with no automatic changes.
*/
const ICONS = { what: MessageSquare, earlier: History, picture: Sparkles, say: MessageSquare };
const POS = {
  what: "left-[4%] top-[6%] w-[62%] sm:w-[58%]",
  earlier: "right-[3%] top-[30%] w-[52%] sm:w-[48%]",
  picture: "left-[5%] top-[49%] w-[60%] sm:w-[56%]",
  say: "right-[4%] bottom-[5%] w-[62%] sm:w-[58%]",
};

export function HeroAnimation() {
  const ref = useRef(null);
  const lottieRef = useRef(null);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [k, setK] = useState(0);
  const [started, setStarted] = useState(reduced);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(reduced);

  useEffect(() => {
    if (reduced || started) return;
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setStarted(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setStarted(true); io.disconnect(); } }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, [reduced, started]);

  useEffect(() => {
    const l = lottieRef.current;
    if (!l) return;
    if (paused) l.pause(); else l.play();
  }, [paused]);

  const replay = () => { setDone(false); setPaused(false); setStarted(true); setK((x) => x + 1); };
  const state = reduced ? "hero-static" : !started ? "hero-waiting" : done ? "hero-settled" : "hero-playing";

  return (
    <div ref={ref} data-testid="hero-animation" className="relative w-full max-w-[520px] mx-auto">
      <div className="relative w-full select-none overflow-hidden sm:aspect-square">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <Lottie lottieRef={lottieRef} animationData={ambient} loop autoplay={!reduced} style={{ width: "100%", height: "100%" }} />
        </div>

        <div key={k} className={`hero-scene absolute inset-0 ${state} ${paused ? "hero-paused" : ""}`} aria-live="off"
             onAnimationEnd={(e) => { if (e.target.classList.contains("hero-card-say")) setDone(true); }}>
          {EXAMPLE.highlights.map((h) => {
            const Icon = ICONS[h.key];
            return (
              <div key={h.key} data-testid={`hero-highlight-${h.key}`}
                   className={`hero-card hero-card-${h.key} absolute ${POS[h.key]} ${h.key === "picture" ? "ss-card-premium" : "ss-card"} p-3.5 sm:p-4 shadow-lg`}
                   style={h.key === "picture" ? { position: "absolute" } : { position: "absolute", background: "rgba(254, 247, 235, 0.96)" }}>
                <div className="flex items-center gap-1.5">
                  <Icon size={11} className="text-mauve shrink-0" />
                  <p className="text-[9px] tracking-[0.3em] uppercase text-mauve">{h.label}</p>
                </div>
                <p className={`mt-1.5 text-cocoa leading-snug ${h.key === "say" ? "italic text-[12px] sm:text-[13px]" : "text-[12px] sm:text-[13px]"}`}>{h.text}</p>
              </div>
            );
          })}

          {/* Quiet chronology illustration — Relationship notes · A. */}
          <div className="hero-report absolute left-[4%] bottom-[6%] w-[34%] sm:w-[32%] ss-card-premium p-2 shadow-xl rotate-[-4deg]" style={{ position: "absolute" }} aria-hidden="true">
            <div className="w-full bg-ivory rounded-md p-2 flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <FileText size={9} className="text-mauve" />
                <span className="text-[7px] tracking-[0.25em] uppercase text-mauve">Relationship notes · {EXAMPLE.nickname}</span>
              </div>
              {EXAMPLE.notes.map((n) => (
                <div key={n.when} className="flex gap-1 items-start">
                  <span className="text-[7px] font-heading text-mauve w-8 shrink-0">{n.when}</span>
                  <span className="text-[7px] text-cocoa/70 leading-tight">{n.what}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none rounded-3xl ring-1 ring-champagne/40" />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-cocoa/55">
        <span><a href="#full-example" data-testid="hero-read-full-example" className="underline underline-offset-2 hover:text-cocoa">Read the full example</a></span>
        {!reduced && (
          <span className="flex gap-1.5">
            <button type="button" data-testid="hero-pause-toggle" onClick={() => setPaused((p) => !p)} aria-pressed={paused}
                    className="ss-pill ss-pill-ghost h-9 min-w-[44px] px-3 text-xs" aria-label={paused ? "Play the example animation" : "Pause the example animation"}>
              {paused ? <Play size={12} /> : <Pause size={12} />} {paused ? "Play" : "Pause"}
            </button>
            <button type="button" data-testid="hero-replay" onClick={replay} className="ss-pill ss-pill-ghost h-9 min-w-[44px] px-3 text-xs" aria-label="Replay the example animation">
              <RotateCcw size={12} /> Replay
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
